import { useEffect, useMemo, useState } from 'react';
import Modal from '../Modal';
import {
  repositorioFabricanteMaquina, repositorioFazenda, repositorioMaquina, repositorioTipoMaquina,
} from '../../sync/repositories/fazendas';
import { extrairMensagensErro } from '../../utils/errorUtils';

const ESTADO_INICIAL = {
  fazenda: '', tipo_maquina: '', fabricante: '', modelo: '', ano_fabricacao: '',
};

/**
 * Modal de cadastro rápido de Máquina — versão resumida do formulário de
 * Gestão de Máquinas (sem km/custo-hora/velocidade, que ficam com os
 * defaults do model e são ajustáveis depois na tela completa).
 */
export default function ModalCadastroMaquina({ isOpen, onClose, onSuccess, fazendaSelecionadaId = null }) {
  const [dados, setDados] = useState(ESTADO_INICIAL);
  const [fazendas, setFazendas] = useState([]);
  const [tiposMaquina, setTiposMaquina] = useState([]);
  const [fabricantes, setFabricantes] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [erros, setErros] = useState([]);

  useEffect(() => {
    if (!isOpen) {
      setDados(ESTADO_INICIAL);
      setErros([]);
      setSalvando(false);
      return;
    }
    repositorioFazenda.listar().then((r) => setFazendas(r.data.results || r.data)).catch(() => setFazendas([]));
    repositorioTipoMaquina.listar().then((r) => setTiposMaquina(r.data.results || r.data)).catch(() => setTiposMaquina([]));
    repositorioFabricanteMaquina.listar().then((r) => setFabricantes(r.data.results || r.data)).catch(() => setFabricantes([]));
    setDados((prev) => ({ ...prev, fazenda: fazendaSelecionadaId || '' }));
  }, [isOpen, fazendaSelecionadaId]);

  const fabricantesFiltrados = useMemo(() => {
    if (!dados.tipo_maquina) return [];
    return fabricantes.filter((f) => (f.tipos_maquina || []).includes(Number(dados.tipo_maquina)) && f.ativo !== false);
  }, [fabricantes, dados.tipo_maquina]);

  const handleChange = (campo) => (e) => {
    const valor = e.target.value;
    setDados((prev) => ({ ...prev, [campo]: valor, ...(campo === 'tipo_maquina' ? { fabricante: '' } : {}) }));
  };

  const handleSalvar = async (e) => {
    e?.preventDefault?.();
    if (!dados.fazenda) { setErros(['Selecione a fazenda.']); return; }
    if (!dados.tipo_maquina) { setErros(['Selecione o tipo de máquina.']); return; }
    if (!dados.fabricante) { setErros(['Selecione o fabricante.']); return; }
    if (!dados.modelo.trim()) { setErros(['Informe o modelo.']); return; }
    if (!dados.ano_fabricacao || String(dados.ano_fabricacao).length !== 4) {
      setErros(['Informe um ano de fabricação válido (4 dígitos).']); return;
    }
    setSalvando(true);
    setErros([]);
    try {
      const resp = await repositorioMaquina.criar({
        fazenda: dados.fazenda,
        tipo_maquina: dados.tipo_maquina,
        fabricante: dados.fabricante,
        modelo: dados.modelo.trim(),
        ano_fabricacao: dados.ano_fabricacao,
      });
      onSuccess?.(resp.data);
      onClose?.();
    } catch (error) {
      setErros(extrairMensagensErro(error));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={salvando ? () => {} : onClose} title="Cadastrar Máquina" size="lg">
      <form onSubmit={handleSalvar} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Fazenda *</label>
          <select
            value={dados.fazenda}
            onChange={handleChange('fazenda')}
            disabled={salvando}
            className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
          >
            <option value="">Selecione...</option>
            {fazendas.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Máquina *</label>
            <select
              value={dados.tipo_maquina}
              onChange={handleChange('tipo_maquina')}
              disabled={salvando}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">Selecione...</option>
              {tiposMaquina.filter((t) => t.ativo !== false).map((t) => <option key={t.id} value={t.id}>{t.nome}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fabricante *</label>
            <select
              value={dados.fabricante}
              onChange={handleChange('fabricante')}
              disabled={salvando || !dados.tipo_maquina}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300 bg-white"
            >
              <option value="">{dados.tipo_maquina ? 'Selecione...' : 'Selecione o tipo primeiro'}</option>
              {fabricantesFiltrados.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Modelo *</label>
            <input
              type="text"
              value={dados.modelo}
              onChange={handleChange('modelo')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              placeholder="Ex: Série 5000, 8R..."
              autoFocus
              disabled={salvando}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ano de Fabricação *</label>
            <input
              type="number"
              value={dados.ano_fabricacao}
              onChange={handleChange('ano_fabricacao')}
              className="w-full border border-gray-300 p-2 rounded focus:ring-2 focus:ring-green-300"
              placeholder="2024"
              disabled={salvando}
            />
          </div>
        </div>
        <p className="text-sm text-gray-600">
          Km de aquisição, custo/hora e velocidade média ficam com os valores padrão — complete na tela de Gestão de Máquinas depois.
        </p>
        {erros.length > 0 && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-3 space-y-1">
            {erros.map((m, i) => <div key={i}>{m}</div>)}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-100 disabled:opacity-50">
            Cancelar
          </button>
          <button type="submit" disabled={salvando}
            className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:bg-blue-300">
            {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
